/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import React from 'react';
import { Typography, Avatar, Tooltip } from '@mui/material';
import { useMaskedIdentity } from '../../../hooks/use-masked-identity';

interface UserIdentityCardProps {
  name?: string;
  phoneNumber: string;
  size?: 'small' | 'medium' | 'large';
  dir?: 'row' | 'column';
}

const UserIdentityCard: React.FC<UserIdentityCardProps> = ({ name, phoneNumber, size = 'medium', dir = 'row' }) => {
  const { nameToRender, phoneNumberToRender } = useMaskedIdentity({
    name,
    phoneNumber,
  });

  const getInitials = (name?: string) => {
    if (!name || name === 'Unknown') return phoneNumber?.charAt(phoneNumber.length - 1) || 'U';
    return name
      .split(' ')
      .map((word) => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const avatarSize = {
    small: { width: 32, height: 32, fontSize: '12px' },
    medium: { width: 40, height: 40, fontSize: '14px' },
    large: { width: 48, height: 48, fontSize: '16px' },
  }[size];

  const nameSize = {
    small: '12px',
    medium: '14px',
    large: '16px',
  }[size];

  const numberSize = {
    small: '10px',
    medium: '12px',
    large: '12px',
  }[size];

  return (
    <div
      css={css`
        display: flex;
        width: 100%;
        flex-direction: ${dir === 'column' ? 'column' : 'row'};
        align-items: center;
        gap: ${dir === 'column' ? '8px' : '12px'};
        text-align: ${dir === 'column' ? 'center' : 'left'};
      `}
    >
      <Avatar
        css={css`
          width: ${avatarSize.width}px;
          height: ${avatarSize.height}px;
          background: #e8f4f8;
          color: #2d2d2d;
          font-size: ${avatarSize.fontSize};
          font-weight: 600;
        `}
      >
        {getInitials(nameToRender)}
      </Avatar>
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: 4px;
          min-width: 0;
          flex: ${dir === 'column' ? 'none' : '1'};
          align-items: ${dir === 'column' ? 'center' : 'flex-start'};
          width: calc(100% - ${avatarSize.width}px - ${dir === 'column' ? '0px' : '12px'});
        `}
      >
        <Tooltip title={nameToRender} placement="top">
          <Typography
            css={css`
              color: #2d2d2d;
              font-size: ${nameSize};
              font-weight: 600;
              line-height: 1.2;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              text-align: ${dir === 'column' ? 'center' : 'left'};
              width: 100%;
            `}
          >
            {nameToRender || 'Unknown'}
          </Typography>
        </Tooltip>
        <Typography
          css={css`
            color: #7a7a7a;
            font-size: ${numberSize};
            font-weight: 500;
            line-height: 1.2;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            text-align: ${dir === 'column' ? 'center' : 'left'};
          `}
        >
          {phoneNumberToRender}
        </Typography>
      </div>
    </div>
  );
};

export default UserIdentityCard;
